import type { Driver } from '@cobliteam/api-lib';
import { type DeviceFragment, DeviceGeneralStatus, DeviceGeneralType, DeviceType, type LastLocationFragment } from '@cobliteam/api-lib/src/__generated__/graphql';

import type { VehicleLastLocation } from '@cobliteam/common-types';
import { differenceInMilliseconds } from '@cobliteam/utils/date';
import type { TimestampInMillis } from './dates';
import type { Coordinate } from './geoJSON';

const REMOVABLE_HARD_RESET_TUTORIAL_VIDEO_EMBED_LINK = 'https://www.youtube.com/embed/pLuGEvXNGVg?si=BHem5jA50Tb1FA-j';
const CAM_HARD_RESET_TUTORIAL_VIDEO_EMBED_LINK = 'https://www.youtube.com/embed/86iBARwoPOE?si=d29LfFPSFi4xa5bu';

export interface LastLocation {
  heading: number;
  ignitionOn: boolean;
  coordinate: Coordinate;
  speed: number;
  date: TimestampInMillis;
  ingestionTimeInMillis?: TimestampInMillis;
  idleOnDurationInMillis?: TimestampInMillis;
  idleOffDurationInMillis?: TimestampInMillis;
  isPlugged?: boolean;
  pluggedUpdateTimestamp?: TimestampInMillis;
}

export enum DeviceVehicleAssociationStatus {
  LOSING_DATA = '0', // device moving with no associated vehicle
  ASSOCIATED = '1',
  NOT_ASSOCIATED = '2',
}

export interface DeviceDelay {
  delayInMillis?: number;
  ingestionDelayInMillis?: number;
}

export interface Destination {
  address: string;
  lat: number;
  lng: number;
}

export interface DriverWithLastLocation extends Driver {
  lastLocation?: LastLocationFragment;
}

const isDevicePlugged = (isPlugged: boolean | undefined | null) => isPlugged === undefined || isPlugged === null || isPlugged;

const getDeviceDelayInMillis = (timeInMillis?: TimestampInMillis) => {
  if (!timeInMillis) return undefined;

  const nowInMillis = new Date();
  return differenceInMilliseconds(nowInMillis, timeInMillis);
};

const getDeviceDelay = (lastLocation?: LastLocation | VehicleLastLocation | LastLocationFragment | null): DeviceDelay => {
  const timeInMillis =
    (lastLocation as LastLocation)?.date || (lastLocation as VehicleLastLocation)?.timeInMillis || (lastLocation as LastLocationFragment)?.timeInMillis || undefined;
  const ingestionTimeInMillis = (lastLocation as LastLocation)?.ingestionTimeInMillis || (lastLocation as VehicleLastLocation)?.cobliIngestedAt || undefined;

  return {
    delayInMillis: getDeviceDelayInMillis(timeInMillis),
    ingestionDelayInMillis: getDeviceDelayInMillis(ingestionTimeInMillis),
  };
};

const getDeviceStatusLabel = (status: DeviceGeneralStatus, t: any): string => {
  switch (status) {
    case DeviceGeneralStatus.Normal:
      return t('device_status.normal');
    case DeviceGeneralStatus.Syncing:
      return t('device_status.syncing');
    case DeviceGeneralStatus.NoSignal:
      return t('device_status.no_signal');
    case DeviceGeneralStatus.Disconnected:
      return t('device_status.disconnected');
    case DeviceGeneralStatus.Unavailable:
      return t('device_status.unavailable');
    case DeviceGeneralStatus.NoSufficientData:
      return t('device_status.no_sufficient_data');
    case DeviceGeneralStatus.LosingData:
      return t('device_status.losing_data');
    case DeviceGeneralStatus.InDevolution:
      return t('device_status.in_devolution');
    default:
      return t('device_status.no_sufficient_data');
  }
};

const getHardResetTutorialVideoLink = (device: DeviceFragment): string => {
  const { type: deviceType } = device;
  const isCam = [DeviceType.Dashcam, DeviceType.Multicam].includes(deviceType);

  if (isCam) return CAM_HARD_RESET_TUTORIAL_VIDEO_EMBED_LINK;

  return REMOVABLE_HARD_RESET_TUTORIAL_VIDEO_EMBED_LINK;
};

const getIgnitionStatusEventLabel = (device: DeviceFragment): string => {
  const { lastLocation } = device;

  if (!lastLocation) return 'no-data';

  return device.lastLocation?.ignitionOn ? 'ignition-on' : 'ignition-off';
};

const formatDeviceStatusEventLabel = (deviceStatus?: DeviceGeneralStatus): string => deviceStatus?.toLocaleLowerCase().replace('_', '-') || '';

const getDeviceStatusEventLabel = (device: DeviceFragment): string => {
  const { generalStatus } = device;
  return formatDeviceStatusEventLabel(generalStatus);
};

const formatGeneralTypeObservationTrigger = (generalType: DeviceGeneralType) => {
  const isRemovable = generalType === DeviceGeneralType.Removable || generalType === DeviceGeneralType.PlugAndPlayIbutton;
  const isCam = [DeviceGeneralType.Dashcam, DeviceGeneralType.Multicam].includes(generalType);

  if (isRemovable) return 'REMOVABLE';
  if (isCam) return 'CAM';
  return 'INSTALLED';
};

export {
  isDevicePlugged,
  getDeviceDelay,
  getDeviceStatusLabel,
  getHardResetTutorialVideoLink,
  getIgnitionStatusEventLabel,
  getDeviceStatusEventLabel,
  formatDeviceStatusEventLabel,
  formatGeneralTypeObservationTrigger,
};
